import { useMemo, useCallback } from 'react';
import { useTranslate } from '@shopify/ui-extensions-react/customer-account';
import {
  getSeatTypeDisplayName,
} from '../utils/seatRequirements';
import {
  UseSeatUploadBannerProps,
  UseSeatUploadBannerReturn,
  GroupedRequirementData,
} from '../types';

/**
 * @description Hook for the seat upload banner
 * @param seatRequirements - The seat requirements
 * @returns An object containing the title, instructions, grouped requirements data, seat type instructions, has multiple items, and multiple items note
 */
export const useSeatUploadBanner = ({
  seatRequirements,
}: UseSeatUploadBannerProps): UseSeatUploadBannerReturn => {
  const translate = useTranslate();

  const { totalPhotosRequired, seatRequirements: requirements } =
    seatRequirements;

  /**
   * @description Groups the requirements by seat type
   * @returns An object containing the grouped requirements
   */
  const groupedRequirements = useMemo(() => {
    return requirements.reduce(
      (groups, req) => {
        if (!groups[req.seatType]) {
          groups[req.seatType] = [];
        }
        groups[req.seatType].push(req);
        return groups;
      },
      {} as Record<string, typeof requirements>,
    );
  }, [requirements]);

  // Simple comparison - no memoization needed
  const hasMultipleItems = requirements.length > 1;

  /**
   * @description Gets the seat type instructions
   * @param seatType - The seat type
   * @returns The seat type instructions
   */
  const getSeatTypeInstructions = useCallback(
    (seatType: string): string => {
      switch (seatType) {
        case 'FIRST_ROW':
          return translate('seatUploadBanner.firstRowInstructions');
        case 'SECOND_THIRD_ROW':
          return translate('seatUploadBanner.secondThirdRowInstructions');
        case 'TWO_ROW_BUNDLE':
          return translate('seatUploadBanner.twoRowBundleInstructions');
        case 'THREE_ROW_BUNDLE':
          return translate('seatUploadBanner.threeRowBundleInstructions');
        default:
          return 'Please upload clear photos of your seats';
      }
    },
    [translate],
  );

  /**
   * @description Groups the requirements by seat type
   * @returns An object containing the grouped requirements data
   */
  const groupedRequirementsData = useMemo<GroupedRequirementData[]>(() => {
    return Object.entries(groupedRequirements).map(([seatType, reqs]) => {
      const displayName = getSeatTypeDisplayName(seatType);
      const totalPhotosForType = reqs.length * reqs[0].photosRequired;
      const itemCount = reqs.length;
      const photosPerItem = reqs[0].photosRequired;

      return {
        seatType,
        displayName,
        totalPhotosForType,
        itemCount,
        photosPerItem,
      };
    });
  }, [groupedRequirements]);

  /**
   * @description Gets the seat type instructions
   * @returns An object containing the seat type instructions
   */
  const seatTypeInstructions = useMemo(() => {
    return Object.keys(groupedRequirements).map((seatType) => ({
      seatType,
      instruction: getSeatTypeInstructions(seatType),
    }));
  }, [groupedRequirements, getSeatTypeInstructions]);

  const title = translate('seatUploadBanner.title');
  const instructions = translate('seatUploadBanner.instructions', {
    totalPhotos: totalPhotosRequired,
  });
  const multipleItemsNote = translate('seatUploadBanner.multipleItemsNote');

  return {
    title,
    instructions,
    groupedRequirementsData,
    seatTypeInstructions,
    hasMultipleItems,
    multipleItemsNote,
  };
};

